<template>
  <v-container class="mirrors-redirectors" fluid>
    <v-row class="mx-0" style="height:48px;">
      <h2 class="mirrors-redirectors__title">
        Mirrors and redirect
      </h2>
    </v-row>
    <div
      class="mx-0 pb-4 d-flex justify-space-between align-end"
    >
      <select-project
        v-model="project"
        style="min-width: 250px; max-width: 250px"
      />

      <v-btn
        v-if="canEditMirrors && activeTab === 'redirectors' && project"
        color="primary"
        class="align-self-center"
        @click="openRedirectorForm()"
      >
        Add redirector
      </v-btn>

      <div
        v-if="showMirrorsSearch"
        class="mirrors-redirectors__search d-flex align-center"
      >
        <div class="input-with-filters mirrors-redirectors__search-field">
          <v-text-field
            v-model="mirrorsSearchArgs.filters.name"
            class="search-input"
            solo
            outlined
            flat
            dense
            :loading="mirrorsSearchLoading"
            prepend-inner-icon="search"
            label="Search"
            hide-details
          >
            <template #append>
              <v-btn
                icon
                color="primary"
                @click="mirrorsSearchState.isFilterDisplayed = !mirrorsSearchState.isFilterDisplayed"
              >
                <v-icon>
                  mdi-tune-vertical
                </v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <v-card
            v-if="mirrorsSearchState.isFilterDisplayed"
            class="filter-card"
            :loading="mirrorsSearchLoading"
          >
            <v-card-title v-text="'Filters'" />
            <v-divider />

            <v-row class="mx-0">
              <v-col cols="12" sm="6" md="6">
                <select-project
                  v-model="mirrorsSearchArgs.project"
                  label="Select Project"
                />
              </v-col>
            </v-row>
            <v-row class="mx-0">
              <v-col cols="12" sm="6" md="6">
                <date-picker
                  v-model="mirrorsSearchArgs.from"
                  label="Date From"
                  style="min-width: 140px"
                  class="date-picker"
                />
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <date-picker
                  v-model="mirrorsSearchArgs.to"
                  label="Date To"
                  style="min-width: 140px"
                  class="date-picker"
                />
              </v-col>
            </v-row>

            <v-card-actions class="justify-end">
              <v-btn
                v-text="'Cancel'"
                color="error"
                text
                :loading="mirrorsSearchLoading"
                @click="mirrorsSearchState.isFilterDisplayed = false"
              />
              <v-btn
                v-text="'Search'"
                color="primary"
                text
                :loading="mirrorsSearchLoading"
                @click="onMirrorsSearchFromFilters"
              />
            </v-card-actions>
          </v-card>
        </div>
        <v-btn
          v-text="'Search'"
          color="primary"
          :disabled="!project || mirrorsSearchLoading"
          :loading="mirrorsSearchLoading"
          @click="onMirrorsSearch"
        />
      </div>
    </div>
    <div class="d-flex align-center">
      <v-tabs
        :show-arrows="$vuetify.breakpoint.name == 'xs' || $vuetify.breakpoint.name == 'sm'"
        :left="$vuetify.breakpoint.name !== 'xs' && $vuetify.breakpoint.name !== 'sm'"
        v-model="activeTab"
        class="flex-grow-1"
      >
        <v-tab href="#main-settings">Main Settings</v-tab>
        <v-tab href="#mirrors-groups">Mirrors Groups</v-tab>
        <v-tab href="#mirrors">Mirrors</v-tab>
        <v-tab href="#redirectors">Redirectors</v-tab>
        <v-btn
          outlined
          small
          color="primary"
          class="ml-auto align-self-center mr-3 px-2"
          :disabled="!project"
          @click="openAuditLog"
        >
          Audit Log
        </v-btn>
      </v-tabs>
    </div>

    <main-settings-tab
      v-if="activeTab === 'main-settings'"
      :project="project"
      :can-edit="canEditMirrors"
      :can-read-feature-config="canReadFeatureConfigs"
    />
    <mirrors-groups-tab
      v-if="activeTab === 'mirrors-groups'"
      ref="mirrorsGroupsTab"
      :project="project"
      :search-args="mirrorsSearchArgs"
      :can-edit="canEditMirrors"
      :can-read-feature-config="canReadFeatureConfigs"
      @show-audit-log="openAuditLog"
    />
    <mirrors-tab
      v-if="activeTab === 'mirrors'"
      ref="mirrorsTab"
      :project="project"
      :search-args="mirrorsSearchArgs"
      :can-edit="canEditMirrors"
      :can-read-feature-config="canReadFeatureConfigs"
      @show-audit-log="openAuditLog"
    />
    <redirectors-tab
      v-if="activeTab === 'redirectors'"
      ref="redirectorsTab"
      :project="project"
      :can-edit="canEditMirrors"
      @data-loaded="onRedirectorsDataLoaded"
      @edit-redirector="openRedirectorForm"
    />

    <drawer-dialog v-model="redirectorFormOpen" :is-loading="redirectorFormSaving" width="70%">
      <redirector-form
        v-if="redirectorFormOpen"
        :project="project"
        :redirector="redirectorForm"
        :mirrors="redirectorsData.mirrors"
        :groups="filteredGroupsForRedirector"
        :is-saving="redirectorFormSaving"
        @save="onSaveRedirectorForm"
        @close="onCloseRedirectorForm"
      />
    </drawer-dialog>

    <drawer-dialog v-model="auditLogOpen" width="70%" max-width="1200px">
      <mirrors-redirectors-audit-log-form
        v-if="auditLogOpen && project"
        :key="auditLogFormKey"
        :entity-id="project"
        :type="auditLogFilterType"
        :mirror-id="auditLogMirrorId"
        @close="closeAuditLog"
      />
    </drawer-dialog>
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
  import DatePicker from '@/components/core/DatePicker/DatePicker.vue'
  import { AUDIT_LOG_TYPE, auditLogTypeFromTab } from '../constants/const'

  export default {
    name: 'MirrorsRedirectors',
    components: {
      SelectProject,
      DatePicker,
      MainSettingsTab: () => import('./MainSettings/MainSettingsTab.vue'),
      MirrorsGroupsTab: () => import('./MirrorsGroups/MirrorsGroupsTab.vue'),
      MirrorsTab: () => import('./Mirrors/MirrorsTab.vue'),
      RedirectorsTab: () => import('./Redirectors/RedirectorsTab.vue'),
      DrawerDialog: () => import('@/components/core/DrawerDialog/DrawerDialog.vue'),
      RedirectorForm: () => import('./Redirectors/RedirectorForm.vue'),
      MirrorsRedirectorsAuditLogForm: () => import('../MirrorsRedirectorsAuditLogForm.vue'),
    },
    data: () => ({
      project: null,
      activeTab: 'main-settings',
      redirectorFormOpen: false,
      redirectorFormSaving: false,
      redirectorForm: null,
      redirectorsData: {
        mirrors: [],
        groups: [],
        redirectors: [],
      },
      mirrorsSearchLoading: false,
      mirrorsSearchArgs: {
        from: null,
        to: null,
        filters: {
          name: '',
        },
        project: null,
      },
      mirrorsSearchState: {
        isFilterDisplayed: false,
      },
      auditLogOpen: false,
      auditLogFilterType: AUDIT_LOG_TYPE.MAIN_SETTINGS,
      auditLogMirrorId: null,
    }),
    watch: {
      activeTab(newVal, oldVal) {
        if (newVal !==  oldVal) {
          this.mirrorsSearchState.isFilterDisplayed = false
          this.mirrorsSearchArgs.filters.name = ''
          this.mirrorsSearchArgs.from = null,
          this.mirrorsSearchArgs.to = null

        }
      },
      project(val) {
        this.mirrorsSearchArgs.project = val
        if (this.auditLogOpen) {
          this.closeAuditLog()
        }
      },
    },

    computed: {
      canEditMirrors() {
        return this.$store.state.auth.checkAccess({ key: 'mirrors_edit', value: true })
      },
      canReadFeatureConfigs() {
        return this.$store.state.auth.checkAccess({ key: 'feature_configs', value: true })
      },
      showMirrorsSearch() {
        return this.activeTab === 'mirrors' || this.activeTab === 'mirrors-groups'
      },
      filteredGroupsForRedirector() {
        const groups = this.redirectorsData.groups ?? []
        const redirectors = this.redirectorsData.redirectors ?? []
        const editingId = this.redirectorForm?.redirector_id ?? null
        const usedGroupIds = new Set(
          redirectors
            .filter((r) => editingId == null || r.redirector_id !== editingId)
            .map((r) => r.group_mirror_id)
            .filter(Boolean),
        )
        return groups.filter((g) => !usedGroupIds.has(g.group_mirror_id))
      },
      auditLogFormKey() {
        return `${this.auditLogFilterType}-${this.auditLogMirrorId ?? 'all'}`
      },
    },
    methods: {
      onRedirectorsDataLoaded({ mirrors, groups, redirectors }) {
        this.redirectorsData = {
          mirrors: Array.isArray(mirrors) ? mirrors : [],
          groups: Array.isArray(groups) ? groups : [],
          redirectors: Array.isArray(redirectors) ? redirectors : [],
        }
      },
      syncRedirectors() {
        const tab = this.$refs.redirectorsTab
        if (!tab) return
        this.redirectorsData = {
          mirrors: tab.mirrors || [],
          groups: tab.groups || [],
          redirectors: tab.redirectors || [],
        }
      },
      openRedirectorForm(redirector) {
        if (!this.project) return
        this.syncRedirectors()
        this.redirectorForm = redirector ? cloneDeep(redirector) : null
        this.redirectorFormOpen = true
      },
      onCloseRedirectorForm() {
        this.redirectorFormOpen = false
        this.redirectorForm = null
      },

      async onSaveRedirectorForm({ redirector }) {
        if (!this.canEditMirrors || !redirector || !this.$refs.redirectorsTab) return
        this.redirectorFormSaving = true
        try {
          await this.$refs.redirectorsTab.saveRedirector(redirector)
          this.onCloseRedirectorForm()
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to save redirector')
        } finally {
          this.redirectorFormSaving = false
        }
      },
      async onMirrorsSearch() {
        if (!this.project) return

        const tabMap = {
          'mirrors':        { ref: 'mirrorsTab',       method: 'loadMirrors'       },
          'mirrors-groups': { ref: 'mirrorsGroupsTab',  method: 'loadMirrorsGroups' },
        }

        const entry = tabMap[this.activeTab]
        if (!entry) return

        const tab = this.$refs[entry.ref]
        if (!tab) return

        this.mirrorsSearchLoading = true
        try {
          await tab[entry.method](this.project)
          const next = this.mirrorsSearchArgs.project
          if (next != null && next !== this.project) {
            this.project = next
          }
        } finally {
          this.mirrorsSearchLoading = false
          this.mirrorsSearchState.isFilterDisplayed = false
        }
      },
      onMirrorsSearchFromFilters() {
        return this.onMirrorsSearch()
      },

      openAuditLog(scope) {
        if (!this.project) return

        if (scope?.mirrorId != null) {
          this.auditLogFilterType = AUDIT_LOG_TYPE.MIRROR
          this.auditLogMirrorId = scope.mirrorId
        } else {
          this.auditLogFilterType = auditLogTypeFromTab(this.activeTab)
          this.auditLogMirrorId = null
        }

        this.auditLogOpen = true
      },

      closeAuditLog() {
        this.auditLogOpen = false
        this.auditLogMirrorId = null
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mirrors-redirectors {

    &__title {
      font-size: 20px;
      line-height: 40px;
      font-weight: 400;
    }

    &__search {
      gap: 16px;

      &-field {
        width: 520px;
      }
    }
  }

  .input-with-filters {
    position: relative;

    .search-input {
      & ::v-deep .v-input__append-inner {
        margin-top: 0;
        align-self: center;
      }
    }
    .filter-card {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      width: 100%;
      z-index: 5;
    }
  }

  .date-picker ::v-deep .date-picker__input {
    padding-left: 0;
  }
</style>
